// apps/patient/src/pages/Login.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { usePatient } from '../contexts/PatientContext';
import { patientApiService } from '../services/api';
import { getEnvironmentConfig, getCurrentPlatform } from '../config/platforms';
import { isOnboardingComplete } from '../utils/onboarding';
import { Button } from '@prism-ehr/shared/components/ui';
import { initializeFaceTecSDK, startVerification } from '@prism-ehr/shared/services/faceTecService';
import { CheckCircle, Phone, Mail } from 'lucide-react';

type LoginStep = 'phone' | 'pin' | 'identity' | 'biometric';

// Kill switch for SMS login (e.g. AWS SNS outage). When true, login is
// email-only with an explanatory banner; no other changes needed.
const SMS_DISABLED = false;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyPin, resolveIdentity, isAuthenticated, patient } = usePatient();

  const config = getEnvironmentConfig();
  const platform = getCurrentPlatform();
  const showSignup = platform.id === 'exercise' || platform.id === 'rehab';

  const [step, setStep] = useState<LoginStep>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [useEmail, setUseEmail] = useState(SMS_DISABLED);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Identity step fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState('');
  const [needsProvider, setNeedsProvider] = useState(false);
  const [providerMessage, setProviderMessage] = useState('');

  // Auth flow data
  const [authData, setAuthData] = useState<{
    userId?: string;
    sessionToken?: string;
    biometricMfa?: boolean;
  }>({});

  // Check for signup success message
  useEffect(() => {
    if (location.state?.signupSuccess && location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state?.email) {
        setEmail(location.state.email);
        setUseEmail(true);
      }
      if (location.state?.phone) {
        setPhone(location.state.phone);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && patient) {
      if (!isOnboardingComplete(patient)) {
        navigate('/onboarding', { replace: true });
      } else {
        const returnTo = location.state?.returnTo || '/';
        navigate(returnTo, { replace: true });
      }
    }
  }, [isAuthenticated, patient, navigate, location]);

  // Initialize FaceTec on mount
  useEffect(() => {
    initializeFaceTecSDK().catch(err => {
      console.error('Failed to initialize FaceTec:', err);
    });
  }, []);

  const formatPhoneDisplay = (digits: string): string => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(digits);
  };

  const handleProgramLinking = async (programId: string) => {
    try {
      const result = await patientApiService.linkProgramToUser(programId);

      if (result.success) {
        sessionStorage.removeItem('pending_program_id');
        navigate('/programs', { replace: true });
      } else {
        console.error('Failed to link program:', result.error);
        sessionStorage.removeItem('pending_program_id');
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Error linking program:', error);
      sessionStorage.removeItem('pending_program_id');
      navigate('/', { replace: true });
    }
  };

  const handlePostLoginNavigation = async (isNewUser?: boolean) => {
    const pendingProgramId = sessionStorage.getItem('pending_program_id');
    const pendingWorkoutPlanId = sessionStorage.getItem('pending_workout_plan_id');
    if (pendingProgramId && !isNewUser) {
      await handleProgramLinking(pendingProgramId);
    } else if (pendingWorkoutPlanId) {
      navigate('/onboarding', { replace: true });
    } else if (isNewUser) {
      navigate('/onboarding', { replace: true });
    } else {
      const returnTo = location.state?.returnTo || '/';
      navigate(returnTo, { replace: true });
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const identifier = useEmail ? { email } : { phone };
      const result = await login(identifier);
      if (result.success) {
        setStep('pin');
      } else {
        setError('Failed to send verification code. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const identifier = useEmail ? { email } : { phone };
      const result = await verifyPin(identifier, pin);
      if (result.success) {
        if (result.needsIdentity) {
          setStep('identity');
        } else if (result.biometricMfa && result.userId && result.sessionToken) {
          setAuthData({
            userId: result.userId,
            sessionToken: result.sessionToken,
            biometricMfa: result.biometricMfa
          });
          setStep('biometric');
        } else if (result.authToken) {
          await handlePostLoginNavigation(result.isNewUser);
        } else {
          setError('Authentication configuration error. Please contact support.');
        }
      } else {
        setError('Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleIdentitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsProvider(false);
    setLoading(true);

    try {
      const result = await resolveIdentity({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        birthday
      });

      if (result.success) {
        await handlePostLoginNavigation(result.isNewUser);
      } else if (result.needsProvider) {
        setNeedsProvider(true);
        setProviderMessage(result.message || 'No matching patient record found. Please contact your healthcare provider.');
      } else {
        setError('Unable to verify your identity. Please try again.');
      }
    } catch (err) {
      setError('Identity verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricVerification = async () => {
    if (!authData.userId || !authData.sessionToken) {
      setError('Missing authentication data. Please start over.');
      setStep('phone');
      return;
    }

    setError('');
    setLoading(true);

    try {
      startVerification(authData.userId, authData.sessionToken, async (result: any) => {
        if (result.success) {
          if (result.auth_token) {
            localStorage.setItem('patient_auth_token', result.auth_token);

            const pendingProgramId = sessionStorage.getItem('pending_program_id');
            const pendingWorkoutPlanId = sessionStorage.getItem('pending_workout_plan_id');
            if (pendingProgramId) {
              try { await patientApiService.linkProgramToUser(pendingProgramId); } catch (e) { /* continue */ }
              sessionStorage.removeItem('pending_program_id');
              window.location.href = '/programs';
            } else if (pendingWorkoutPlanId) {
              window.location.href = '/onboarding';
            } else {
              window.location.href = '/';
            }
          }
        } else {
          setError('Biometric verification failed. Please try again.');
          setLoading(false);
        }
      });
    } catch (err) {
      setError('Failed to start biometric verification.');
      setLoading(false);
    }
  };

  const resetToStart = () => {
    setStep('phone');
    setPin('');
    setError('');
    setFirstName('');
    setLastName('');
    setBirthday('');
    setNeedsProvider(false);
    setProviderMessage('');
    setAuthData({});
  };

  if (loading && !step) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderStep = () => {
    switch (step) {
      case 'phone':
        return (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            {successMessage && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            )}

            {SMS_DISABLED && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Text message codes are temporarily unavailable. Please sign in with your email address.
                </p>
              </div>
            )}

            {useEmail ? (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    placeholder="Enter your email"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone number
                </label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    required
                    value={formatPhoneDisplay(phone)}
                    onChange={handlePhoneChange}
                    className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            <Button
              type="submit"
              loading={loading}
              fullWidth
              disabled={useEmail ? !email : phone.length !== 10 || loading}
              style={{ backgroundColor: config.themeColor }}
            >
              Send Verification Code
            </Button>

            {!SMS_DISABLED && (
              <button
                type="button"
                onClick={() => {
                  setUseEmail(!useEmail);
                  setError('');
                }}
                className="w-full text-sm hover:opacity-80"
                style={{ color: config.themeColor }}
              >
                {useEmail ? 'Use phone number instead' : 'Use email instead'}
              </button>
            )}
          </form>
        );

      case 'pin':
        return (
          <form onSubmit={handlePinSubmit} className="space-y-6">
            <div>
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700">
                Verification Code
              </label>
              <p className="mt-1 text-sm text-gray-500">
                We sent a code to {useEmail ? email : formatPhoneDisplay(phone)}
              </p>
              <input
                id="pin"
                name="pin"
                type="text"
                autoComplete="one-time-code"
                required
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                placeholder="Enter 6-digit code"
                maxLength={6}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}

            <Button
              type="submit"
              loading={loading}
              fullWidth
              disabled={!pin || loading}
              style={{ backgroundColor: config.themeColor }}
            >
              Verify Code
            </Button>

            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setPin('');
                setError('');
              }}
              className="w-full text-sm hover:opacity-80"
              style={{ color: config.themeColor }}
            >
              {useEmail ? 'Use a different email' : 'Use a different phone number'}
            </button>
          </form>
        );

      case 'identity':
        return (
          <form onSubmit={handleIdentitySubmit} className="space-y-6">
            {needsProvider ? (
              <div className="text-center space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">{providerMessage}</p>
                </div>
                <Button
                  type="button"
                  onClick={resetToStart}
                  fullWidth
                  disabled={false}
                  style={{ backgroundColor: config.themeColor }}
                >
                  Start Over
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    Verify Your Identity
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Please enter your information so we can find your account.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="birthday" className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    id="birthday"
                    type="date"
                    required
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}

                <Button
                  type="submit"
                  loading={loading}
                  fullWidth
                  disabled={!firstName.trim() || !lastName.trim() || !birthday || loading}
                  style={{ backgroundColor: config.themeColor }}
                >
                  Verify Identity
                </Button>

                <button
                  type="button"
                  onClick={resetToStart}
                  className="w-full text-sm hover:opacity-80"
                  style={{ color: config.themeColor }}
                >
                  Start over
                </button>
              </>
            )}
          </form>
        );

      case 'biometric':
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                Biometric Verification
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Please follow the on-screen instructions to verify your identity
              </p>
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center">{error}</div>
            )}

            <Button
              onClick={handleBiometricVerification}
              loading={loading}
              fullWidth
              disabled={loading}
              style={{ backgroundColor: config.themeColor }}
            >
              {loading ? 'Verifying...' : 'Start Biometric Verification'}
            </Button>

            <button
              type="button"
              onClick={resetToStart}
              className="w-full text-sm hover:opacity-80"
              style={{ color: config.themeColor }}
              disabled={loading}
            >
              Start over
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          {/* Dynamic header */}
          <div className="text-center mb-8">
            <h2
              className="text-3xl font-bold"
              style={{ color: config.themeColor }}
            >
              {config.appName}
            </h2>
            <p className="mt-2 text-gray-600">
              {showSignup ? 'Sign in or create an account' : 'Please sign in to your account'}
            </p>
          </div>

          {renderStep()}

          {/* Signup link - only show for exercise/rehab platforms */}
          {showSignup && step === 'phone' && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">New here?</span>
                </div>
              </div>

              <Link
                to="/signup"
                className="block w-full py-3 px-4 text-center border rounded-lg font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{
                  borderColor: config.themeColor,
                  color: config.themeColor
                }}
              >
                Create Account
              </Link>

              <p className="mt-4 text-center text-xs text-gray-500">
                By signing in or creating an account, you agree to our{' '}
                <a
                  href="https://ready2exercise.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ color: config.themeColor }}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://ready2exercise.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                  style={{ color: config.themeColor }}
                >
                  Privacy Policy
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}